/**
 * 核心类型 —— 对拍 climb-core/src/lib.rs:57-184
 * Hold.id == holds 数组下标（不变量，undo 用整表快照）
 */

export enum HoldKind {
  Colored = "Colored",
  Black = "Black",
  White = "White",
  Gray = "Gray",
}

export interface Hold {
  id: number;
  /** 引用 Analysis.labels 的分量标签（回退路径）；0 = 自包含像素 */
  label: number;
  /** 自包含像素（分析分辨率行优先索引） */
  ownPixels: Uint32Array;
  area: number;
  /** bbox [x0, y0, x1, y1]（x1/y1 开区间，分析坐标） */
  bbox: [number, number, number, number];
  centroid: [number, number];
  /** 众数色相（度，显示命名用） */
  hue: number;
  /** 彩色像素 Lab 色度均值 (a*, b*) */
  ab: [number, number];
  sat: number;
  val: number;
  purity: number;
  kind: HoldKind;
  /** 所属颜色组索引；-1 = 未分组（Rust usize::MAX） */
  cluster: number;
  lasso: boolean;
}

export interface Cluster {
  kind: HoldKind;
  hue: number;
  /** 面积加权 Lab 色度中心 */
  ab: [number, number];
  rgb: [number, number, number];
  holds: number[];
  area: number;
}

/** 可调参数（对拍 Params::default） */
export interface Params {
  /** 分析分辨率上限（长边） */
  analysisMaxDim: number;
  /** 墙面差分灵敏度倍数 */
  wallK: number;
  /** 岩点最小面积（占分析图比例） */
  minAreaFrac: number;
  /** 非彩色岩点最大面积 */
  maxAreaFrac: number;
  /** 彩色岩点/体积块最大面积 */
  maxColoredAreaFrac: number;
  /** bbox 填充率下限 */
  minFillRatio: number;
  /** 确认为彩色岩点所需轮廓内彩色像素比例 */
  minColoredFrac: number;
  /** 颜色聚类容差（Lab 色度平面欧氏距离） */
  colorTolAb: number;
  /** 保留掩码膨胀半径（分析分辨率 px） */
  maskDilate: number;
  /** 掩码羽化半径 */
  feather: number;
  /** 背景保留饱和度（0=完全灰度） */
  bgSat: number;
  /** 墙面净化强度（0=关闭） */
  bgFill: number;
  /** 描边高亮强度（0=关闭） */
  outline: number;
  /** 攀岩者保色（1=保色 0=随背景灰度） */
  personKeep: number;
}

export function defaultParams(): Params {
  return {
    analysisMaxDim: 1600,
    wallK: 5.5,
    minAreaFrac: 1.2e-4,
    maxAreaFrac: 0.025,
    maxColoredAreaFrac: 0.07,
    minFillRatio: 0.3,
    minColoredFrac: 0.15,
    colorTolAb: 14.0,
    maskDilate: 2,
    feather: 2.5,
    bgSat: 0.0,
    bgFill: 0.0,
    outline: 0.0,
    personKeep: 1.0,
  };
}

/** 一次完整分析的结果（对拍 Analysis；img 为分析分辨率 RGBA） */
export interface Analysis {
  aw: number;
  ah: number;
  fw: number;
  fh: number;
  /** 回退路径分量标签图；YOLO 主路为空数组 */
  labels: Uint32Array;
  holds: Hold[];
  clusters: Cluster[];
  /** 分析分辨率 RGBA */
  img: Uint8ClampedArray;
  hue: Float32Array;
  sat: Float32Array;
  val: Float32Array;
  /** 攀岩者掩码像素索引；null = 未检出/未启用 */
  person: Uint32Array | null;
  wallV: number;
  wallS: number;
  timingMs: number;
}

/** 彩色像素判定（conn.rs:181 / 渲染颜色约束共用口径）：sat≥0.22 && v≥0.10 */
export function isColoredPx(s: number, v: number): boolean {
  return s >= 0.22 && v >= 0.1;
}
